import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Search, Plus, Pencil, Trash2, Building2 } from 'lucide-react'
import { Faculty } from '@/models/faculty.model'
import { facultiesService, FacultyMutationPayload } from '@/services/facultiesService'
import { FacultyFormDialog, FacultyFormValues } from '@/components/app/faculties/FacultyFormDialog'
import { ConfirmDeleteDialog } from '@/components/app/common/ConfirmDeleteDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { PaginationMeta } from '@/models/templates/pagination.model'

interface FeedbackBanner {
  type: 'success' | 'error'
  message: string
}

export const FacultiesPage: React.FC = () => {
  const [faculties, setFaculties] = useState<Faculty[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [pagination, setPagination] = useState<{ page: number; perPage: number }>({ page: 1, perPage: 12 })
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [banner, setBanner] = useState<FeedbackBanner | null>(null)
  const [formOpen, setFormOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [selectedFaculty, setSelectedFaculty] = useState<Faculty | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  const [facultyToDelete, setFacultyToDelete] = useState<Faculty | null>(null)

  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.perPage)) : 1

  const showBanner = (type: 'success' | 'error', message: string) => {
    setBanner({ type, message })
  }

  const loadFaculties = useCallback(
    async (page: number = pagination.page, perPage: number = pagination.perPage) => {
      setIsLoading(true)
      try {
        const response = await facultiesService.getFacultiesPage({ page, perPage })
        setFaculties(response.data)
        setMeta(response.meta)
      } catch (error) {
        showBanner('error', (error as Error).message || 'Failed to load faculties.')
      } finally {
        setIsLoading(false)
      }
    },
    [pagination.page, pagination.perPage],
  )

  useEffect(() => {
    loadFaculties(pagination.page, pagination.perPage)
  }, [loadFaculties, pagination.page, pagination.perPage])

  useEffect(() => {
    if (!banner) return
    const timeout = setTimeout(() => setBanner(null), 4000)
    return () => clearTimeout(timeout)
  }, [banner])

  const filteredFaculties = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      return faculties
    }

    return faculties.filter((faculty) =>
      faculty.name.toLowerCase().includes(term) || (faculty.description ?? '').toLowerCase().includes(term),
    )
  }, [faculties, searchTerm])

  const openCreateModal = () => {
    setFormMode('create')
    setSelectedFaculty(null)
    setFormError(null)
    setFormOpen(true)
  }

  const openEditModal = (faculty: Faculty) => {
    setFormMode('edit')
    setSelectedFaculty(faculty)
    setFormError(null)
    setFormOpen(true)
  }

  const openDeleteDialog = (faculty: Faculty) => {
    setFacultyToDelete(faculty)
    setDeleteOpen(true)
  }

  const handleFormSubmit = async (values: FacultyFormValues) => {
    const payload: FacultyMutationPayload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      is_active: values.isActive,
    }

    try {
      setSubmitting(true)
      setFormError(null)
      if (formMode === 'create') {
        await facultiesService.createFaculty(payload)
        showBanner('success', 'Faculty created successfully.')
        setPagination((prev) => ({ ...prev, page: 1 }))
        await loadFaculties(1, pagination.perPage)
      } else if (selectedFaculty) {
        await facultiesService.updateFaculty(selectedFaculty.id, payload)
        showBanner('success', 'Faculty updated successfully.')
        await loadFaculties()
      }
      setFormOpen(false)
      setFormError(null)
    } catch (error) {
      const message = (error as Error).message || 'Unable to save faculty.'
      setFormError(message)
      showBanner('error', message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteFaculty = async () => {
    if (!facultyToDelete) return

    try {
      setSubmitting(true)
      await facultiesService.deleteFaculty(facultyToDelete.id)
      showBanner('success', 'Faculty deleted successfully.')
      setDeleteOpen(false)
      await loadFaculties()
    } catch (error) {
      showBanner('error', (error as Error).message || 'Unable to delete faculty.')
    } finally {
      setSubmitting(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value)
    setPagination({ page: 1, perPage: value })
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 flex items-center gap-2">
            Faculties
            <Building2 className="h-6 w-6 text-[#7b2a40]" />
          </h1>
          <p className="text-secondary-600 mt-1">Organise the faculties that group your departments.</p>
        </div>
        <Button onClick={openCreateModal} className="self-start">
          <Plus className="mr-2 h-4 w-4" />
          Create Faculty
        </Button>
      </header>

      {banner && (
        <div
          className={cn(
            'rounded-md border px-4 py-3 text-sm',
            banner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700',
          )}
        >
          {banner.message}
        </div>
      )}

      <section className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
          <Input
            placeholder="Search faculties…"
            className="pl-9"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value)
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
            aria-label="Search faculties"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={pagination.perPage}
            onChange={handlePerPageChange}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
            aria-label="Items per page"
          >
            {[6, 12, 24, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </section>

      {isLoading ? (
        <div className="flex justify-center py-20 text-secondary-500">Loading faculties…</div>
      ) : filteredFaculties.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-secondary-600">
            No faculties found. {searchTerm ? 'Try a different search term.' : 'Create your first faculty to get started.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredFaculties.map((faculty) => (
            <Card key={faculty.id} className="flex flex-col border border-secondary-200">
              <CardHeader className="pb-4 rounded-t-md bg-primary bg-gradient-to-r from-[#7b2a40]/90 to-[#c4788a]/70 min-h-[120px]">
                <CardTitle className="text-lg text-white">{faculty.name}</CardTitle>
                <CardDescription className="line-clamp-3 text-white/70">
                  {faculty.description || 'No description provided.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <Badge className={cn(faculty.is_active ? 'bg-green-100 text-green-800' : 'bg-secondary-100 text-secondary-700')}>
                  {faculty.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <p className="text-xs text-secondary-400">
                  Updated {faculty.updatedAt ? new Date(faculty.updatedAt).toLocaleDateString() : 'Recently'}
                </p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" className="flex-1" onClick={() => openEditModal(faculty)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => openDeleteDialog(faculty)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FacultyFormDialog
        open={formOpen}
        mode={formMode}
        initialValues={
          selectedFaculty
            ? {
                name: selectedFaculty.name,
                description: selectedFaculty.description ?? '',
                isActive: selectedFaculty.is_active,
              }
            : undefined
        }
        submitting={submitting}
        errorMessage={formError}
        onSubmit={handleFormSubmit}
        onClose={() => {
          setFormOpen(false)
          setFormError(null)
        }}
      />

      <ConfirmDeleteDialog
        open={deleteOpen}
        entityLabel="Faculty"
        entityName={facultyToDelete?.name}
        onConfirm={handleDeleteFaculty}
        onClose={() => setDeleteOpen(false)}
        loading={submitting}
      />

      <PaginationControls
        page={pagination.page}
        totalPages={totalPages}
        meta={meta}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

interface PaginationControlsProps {
  page: number
  totalPages: number
  meta: PaginationMeta | null
  onPageChange: (page: number) => void
}

const PaginationControls: React.FC<PaginationControlsProps> = ({ page, totalPages, meta, onPageChange }) => {
  if (!meta) return null

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-secondary-200 pt-4 text-sm text-secondary-600 md:flex-row">
      <div>
        Showing page {page} of {totalPages} • Total {meta.total} faculties
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  )
}

export default FacultiesPage
